import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { updateUser, userIdQueryKey, userTableQueryKey, type User, type UserUpdateRequest } from '@/api/admin/user';
import { type ApiResponseSingle } from '@/api/tenantClient';
import BaseFormInputField from '@/base/Form/InputField';
import BaseFormTextareaField from '@/base/Form/TextareaField';
import BaseFormSelectField, { type Option } from '@/base/Form/SelectField';
import RichTextAreaField from '@/base/Form/RichTextAreaField';
import ImageSelectField from '@/base/Form/ImageSelectField';
import { type FileOrUrl } from '@/base/ImageSelect/ImageSelectItem';
import BaseFormFieldGroup from '@/base/Form/FieldGroup';
import BaseFormDrawer from '@/base/Form/Drawer';
import BaseMessageBarError from '@/base/MessageBar/Error';
import {
  UiStack,
  UiText,
  uiStyles
} from '@/lib/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Formik, type FormikHelpers } from 'formik';
import { useCallback, useState, type FC } from 'react';
import * as Yup from 'yup';

export interface UserEditDrawerProps {
  isOpen: boolean
  onClose: () => void
  user: User
}

export interface FormData {
  firstName: string
  lastName: string
  phone: string
  company: string
  location: string
  clientId: string
  emailCcAddresses: string
  pronoun: string
  gender: string
  bio: string
  jobTitle: string
  companyWebsite: string
  facebook: string
  twitter: string
  linkedin: string
  instagram: string
  wechat: string
  avatar: FileOrUrl[]
  profile: FileOrUrl[]
}

const formSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phone: Yup.string(),
  company: Yup.string(),
  location: Yup.string(),
  clientId: Yup.string(),
  emailCcAddresses: Yup.string(),
  pronoun: Yup.string(),
  gender: Yup.string(),
  bio: Yup.string(),
  jobTitle: Yup.string(),
  companyWebsite: Yup.string().url('Must be a valid URL'),
  facebook: Yup.string().url('Must be a valid URL'),
  twitter: Yup.string().url('Must be a valid URL'),
  linkedin: Yup.string().url('Must be a valid URL'),
  instagram: Yup.string().url('Must be a valid URL'),
  wechat: Yup.string()
});

const genderOptions: Option[] = [
  { value: '', label: 'Select gender' },
  { value: 'not_saying', label: 'Prefer not to say' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' }
];

const UserEditDrawer: FC<UserEditDrawerProps> = ({
  isOpen,
  onClose,
  user
}) => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const queryClient = useQueryClient();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const { reportToGlobal } = useApiErrorHandler();

  const { mutate, isLoading } = useMutation<ApiResponseSingle<User>, Error, UserUpdateRequest>({
    mutationFn: async (data: UserUpdateRequest) => {
      setSaveErrors([]);
      return await updateUser(createTenantAdminApiRequest)(data);
    },
    onSuccess: (result) => {
      if (result?.errors && Array.isArray(result?.errors) && result?.errors.length > 0) {
        setSaveErrors(result?.errors);
      } else {
        setSaveErrors([]);
        void queryClient.invalidateQueries({ queryKey: [userIdQueryKey, { id: user.id }] });
        void queryClient.invalidateQueries({ queryKey: [userTableQueryKey] });
        onClose();
      }
    },
    onError: (error) => {
      reportToGlobal(error);
      setSaveErrors([error.message ?? 'Failed to update user.']);
    }
  });

  const toBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const onSubmit = useCallback(async (
    values: FormData,
    { setSubmitting }: FormikHelpers<FormData>
  ) => {
    setSubmitting(true);
    
    const imageUpdates: { tmpAvatar?: string; tmpProfile?: string } = {};
    
    if (user?.avatar !== values.avatar?.[0]) {
      if (values.avatar?.[0]) {
        imageUpdates.tmpAvatar = values.avatar[0] instanceof File 
          ? await toBase64(values.avatar[0]) 
          : values.avatar[0] as string;
      } else {
        imageUpdates.tmpAvatar = '';
      }
    }
    
    if (user?.profile !== values.profile?.[0]) {
      if (values.profile?.[0]) {
        imageUpdates.tmpProfile = values.profile[0] instanceof File 
          ? await toBase64(values.profile[0]) 
          : values.profile[0] as string;
      } else {
        imageUpdates.tmpProfile = '';
      }
    }
    
    const updateData: UserUpdateRequest = {
      id: user.id,
      user: {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || undefined,
        company: values.company || undefined,
        location: values.location || undefined,
        clientId: values.clientId || undefined,
        emailCcAddresses: values.emailCcAddresses || undefined,
        pronoun: values.pronoun || undefined,
        gender: values.gender || undefined,
        bio: values.bio || undefined,
        jobTitle: values.jobTitle || undefined,
        companyWebsite: values.companyWebsite || undefined,
        facebook: values.facebook || undefined,
        twitter: values.twitter || undefined,
        linkedin: values.linkedin || undefined,
        instagram: values.instagram || undefined,
        wechat: values.wechat || undefined,
        ...imageUpdates
      }
    };
    mutate(updateData);
    setSubmitting(false);
  }, [user.id, mutate]);

  const initialValues: FormData = {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phone: user.phone || '',
    company: user.company || '',
    location: user.location || '',
    clientId: user.clientId || '',
    emailCcAddresses: user.emailCcAddresses || '',
    pronoun: user.pronoun || '',
    gender: user.gender || '',
    bio: user.bio || '',
    jobTitle: user.jobTitle || '',
    companyWebsite: user.companyWebsite || '',
    facebook: user.facebook || '',
    twitter: user.twitter || '',
    linkedin: user.linkedin || '',
    instagram: user.instagram || '',
    wechat: user.wechat || '',
    avatar: user.avatar ? [user.avatar] : [],
    profile: user.profile ? [user.profile] : []
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ values, isSubmitting }) => (
        <BaseFormDrawer
          isOpen={isOpen}
          onClose={onClose}
          title="Edit User Information"
          size="xl"
          isLoading={isSubmitting || isLoading}
        >
          {saveErrors.length > 0 && (
            <UiStack spacing={4} flexGrow={1} py={4}>
              {saveErrors.map((error, index) => (
                <BaseMessageBarError key={index}>
                  {error}
                </BaseMessageBarError>
              ))}
            </UiStack>
          )}
          
          <BaseFormFieldGroup>
            <BaseFormInputField
                  name="firstName"
                  label="First Name"
                  placeholder="Enter first name"
                  isRequired={true}
                />
                
                <BaseFormInputField
                  name="lastName"
                  label="Last Name"
                  placeholder="Enter last name"
                  isRequired={true}
                />
                
                <BaseFormInputField
                  name="phone"
                  label="Phone"
                  placeholder="Enter phone number"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="location"
                  label="Location"
                  placeholder="Enter location"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="pronoun"
                  label="Pronoun"
                  placeholder="e.g., he/him, she/her, they/them"
                  isRequired={false}
                />
                
                <BaseFormSelectField
                  name="gender"
                  label="Gender"
                  options={genderOptions}
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="emailCcAddresses"
                  label="CC Email Addresses"
                  placeholder="Comma-separated email addresses"
                  isRequired={false}
                />
            
            <ImageSelectField
                  name="avatar"
                  label="Avatar"
                  isRequired={false}
                  helperText="Upload a profile avatar image (jpg, jpeg, png, gif). Max size: 10MB"
                />
                
                <ImageSelectField
                  name="profile"
                  label="Profile Image"
                  isRequired={false}
                  helperText="Upload a profile image (jpg, jpeg, png, gif). Max size: 10MB"
                />
            
            <RichTextAreaField
                  name="bio"
                  label="Bio"
                  placeholder="Enter bio"
                  isRequired={false}
                />
            
            <BaseFormInputField
                  name="company"
                  label="Company"
                  placeholder="Enter company name"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="jobTitle"
                  label="Job Title"
                  placeholder="Enter job title"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="companyWebsite"
                  label="Company Website"
                  placeholder="https://example.com"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="clientId"
                  label="Membership ID"
                  placeholder="Enter membership ID"
                  isRequired={false}
                />
            
            <BaseFormInputField
                  name="facebook"
                  label="Facebook"
                  placeholder="https://facebook.com/username"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="twitter"
                  label="Twitter"
                  placeholder="https://twitter.com/username"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="linkedin"
                  label="LinkedIn"
                  placeholder="https://linkedin.com/in/username"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="instagram"
                  label="Instagram"
                  placeholder="https://instagram.com/username"
                  isRequired={false}
                />
                
                <BaseFormInputField
                  name="wechat"
                  label="WeChat"
                  placeholder="WeChat ID"
                  isRequired={false}
                />
          </BaseFormFieldGroup>
        </BaseFormDrawer>
      )}
    </Formik>
  );
};

export default UserEditDrawer;